/**
 * useOnlineStatus — internet ulanish holatini kuzatish
 *
 * Strategiya:
 *   1. navigator.onLine — birinchi indikator (lekin ishonchli emas)
 *   2. Periodic ping (har 30s) — server'ga GET /api/v1/health
 *   3. window 'online' va 'offline' eventlari
 *
 * Return: { isOnline, lastChecked }
 */

import { useEffect, useState } from 'react';

const PING_INTERVAL_MS = 30_000;
const PING_TIMEOUT_MS = 5_000;

interface OnlineStatus {
  isOnline: boolean;
  lastChecked: number | null;
}

async function pingServer(): Promise<boolean> {
  try {
    const controller = new AbortController();
    const timeoutId = setTimeout(() => {
      controller.abort();
    }, PING_TIMEOUT_MS);

    const response = await fetch('/api/v1/health', {
      method: 'GET',
      signal: controller.signal,
      cache: 'no-store',
    });
    clearTimeout(timeoutId);
    return response.ok;
  } catch {
    return false;
  }
}

export function useOnlineStatus(): OnlineStatus {
  const [isOnline, setIsOnline] = useState<boolean>(() => {
    return typeof navigator !== 'undefined' ? navigator.onLine : true;
  });
  const [lastChecked, setLastChecked] = useState<number | null>(null);

  useEffect(() => {
    let mounted = true;

    const handleOnline = (): void => {
      if (mounted) setIsOnline(true);
    };
    const handleOffline = (): void => {
      if (mounted) setIsOnline(false);
    };

    window.addEventListener('online', handleOnline);
    window.addEventListener('offline', handleOffline);

    // Ping server periodically (faqat browser online deydigan paytda)
    const checkServer = async (): Promise<void> => {
      if (!navigator.onLine) {
        if (mounted) {
          setIsOnline(false);
          setLastChecked(Date.now());
        }
        return;
      }
      const ok = await pingServer();
      if (mounted) {
        setIsOnline(ok);
        setLastChecked(Date.now());
      }
    };

    void checkServer();
    const intervalId = setInterval(() => void checkServer(), PING_INTERVAL_MS);

    return () => {
      mounted = false;
      window.removeEventListener('online', handleOnline);
      window.removeEventListener('offline', handleOffline);
      clearInterval(intervalId);
    };
  }, []);

  return { isOnline, lastChecked };
}
